import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { TbHome } from "react-icons/tb";
import { HiOutlineSpeakerphone } from "react-icons/hi";
import { IoIosHeartEmpty } from "react-icons/io";
import { RxGear } from "react-icons/rx";
import { AiOutlineLogout } from "react-icons/ai";
import { logout } from "../../../store/publicStore/actions/utilsActions/utilsActions";
import { FaPhotoVideo, FaWallet, FaYoutube } from "react-icons/fa";
import { MdOutlinePendingActions } from "react-icons/md";
import { SidebarImg } from "../../../static/constant/imgConstants";
import { MdLiveTv } from "react-icons/md";

const Sidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const getUserInfoReducer = useSelector((state) => state.getUserInfoReducer);

  const isSelected = (...paths) => paths.includes(location.pathname);

  // Determine user type
  const userType = getUserInfoReducer?.data?.userType;

  // Sidebar data state
  const [sidebarData, setSidebarData] = useState([]);

  useEffect(() => {
    if (userType === "BRAND") {
      setSidebarData([
        {
          id: "1",
          title: "Home",
          icon: <TbHome size={20} className="mx-[1.8rem]" />,
          path: "/",
          isActive: true,
          subMenuItems: [],
        },
        {
          id: "2",
          title: "Campaigns",
          icon: <HiOutlineSpeakerphone size={20} className="mx-[1.8rem]" />,
          path: "/campaigns",
          isActive: false,
          subMenuItems: [],
        },
        {
          id: "3",
          title: "Wishlist",
          icon: <IoIosHeartEmpty size={20} className="mx-[1.8rem]" />,
          path: "/wishlist",
          isActive: false,
          subMenuItems: [],
        },
        {
          id: "4",
          title: "Settings",
          icon: <RxGear size={20} className="mx-[1.8rem]" />,
          path: "/settings",
          isActive: false,
          subMenuItems: [
            { id: "1", title: "Account Details" },
            { id: "2", title: "Shipping Address" },
            { id: "3", title: "Miscellaneous Details" },
            { id: "4", title: "Social Accounts" },
            { id: "5", title: "Payout Settings" },
          ],
        },
      ]);
    } else if (userType === "INFLUENCER") {
      setSidebarData([
        {
          id: "1",
          title: "Home",
          icon: <TbHome size={20} className="mx-[1.8rem]" />,
          path: ["/influencer"],
          isActive: true,
          subMenuItems: [],
        },
        {
          id: "2",
          title: "Transaction",
          icon: <FaWallet size={20} className="mx-[1.8rem]" />,
          path: ["/influencer/transactions"],
          isActive: false,
          subMenuItems: [],
        },
        {
          id: "3",
          title: "Campaigns",
          icon: <HiOutlineSpeakerphone size={20} className="mx-[1.8rem]" />,
          path: [
            "/influencer/campaigns",
            "/influencer/campaigns/details",
            "/influencer/campaigns/upload",
          ],
          isActive: false,
          subMenuItems: [],
        },
        {
          id: "4",
          title: "UGC Content",
          icon: <FaPhotoVideo size={20} className="mx-[1.8rem]" />,
          path: [
            "/influencer/approved-list",
            "/influencer/campaigns/ugc-upload",
          ],
          isActive: false,
          subMenuItems: [],
        },
        {
          id: "5",
          title: "Unpublished Contents",
          icon: <FaYoutube size={20} className="mx-[1.8rem]" />,
          path: ["/influencer/youtube-not-published-videos"],
          isActive: false,
          subMenuItems: [],
        },
        {
          id: "6",
          title: "Knowledge Base",
          externalLink: true,
          icon: <MdOutlinePendingActions size={22} className="mx-[1.8rem]" />,
          path: ["https://www.paidpromo.ai/knowledge"],
          isActive: false,
          subMenuItems: [],
        },
        {
          id: "7",
          title: "My Channels",
          icon: <MdLiveTv size={20} className="mx-[1.8rem]" />,
          path: ["/influencer/my-channels"],
          isActive: false,
          subMenuItems: [],
        },
      ]);
    }
  }, [userType]);

  // Handle click to toggle `isActive`
  const handleItemClick = (id) => {
    setSidebarData((prevState) =>
      prevState.map((item) =>
        item.id === id
          ? { ...item, isActive: !item.isActive }
          : { ...item, isActive: false }
      )
    );
  };

  const renderSidebarContent = () => (
    <div className="flex flex-col justify-start flex-grow w-full">
      <div className="lg:hidden flex flex-col items-center mb-8 border-b pb-6">
        <div className="w-[6rem] h-[6rem] rounded-full mb-4">
          <img
            className="w-full h-full rounded-full object-cover"
            src={SidebarImg.profile}
            alt="User Avatar"
          />
        </div>
        <span className="text-[1.6rem] font-semibold mb-4">
          {getUserInfoReducer?.data?.firstName}
        </span>
        <button
          className="w-[40%] md:w-[40%] h-[4rem] flex justify-center items-center bg-[#0D9B50] hover:bg-[#6ec396] rounded-full"
          onClick={() => {
            getUserInfoReducer.data.userType === "BRAND"
              ? navigate("/brand/transactions")
              : navigate("/influencer/transactions");
            onClose();
          }}
        >
          <FaWallet size={20} color="white" className="mx-2" />
          <span className="text-[#ffffff] text-[1.4rem]">
            ${getUserInfoReducer?.data?.walletBalance}
          </span>
        </button>
      </div>
      <div className="flex flex-col justify-start items-start w-full h-max">
        {sidebarData?.map((item) => (
          <div
            key={item.id}
            className="flex flex-col justify-start items-start w-full h-max"
          >
            <div
              className={`bs-navs flex justify-start items-center w-full h-[5rem] text-[1.8rem] my-[.2rem] cursor-pointer ${
                item.path.includes(location.pathname)
                  ? "text-[#FFF] bg-[#0D9B50]"
                  : "text-[#202224]"
              } rounded-[.8rem] hover:bg-[#0D9B50] hover:text-[#FFF]`}
              onClick={() => {
                handleItemClick(item.id);
                if (item.externalLink) {
                  window.open(item.path[0], "_blank");
                } else {
                  navigate(item.path[0]);
                }
                onClose();
              }}
            >
              {item.icon}
              <h4 className="">{item.title}</h4>
            </div>
            {item.subMenuItems?.length > 0 &&
              item.isActive &&
              item.subMenuItems.map((subItem) => (
                <div
                  key={subItem.id}
                  className={`bs-sub-navs flex justify-start items-center w-[90%] h-[5rem] ml-[8%] mr-[2%] px-[1.8rem] text-[1.8rem] my-[.2rem] cursor-pointer ${
                    item.isActive ? "bg-[#0D9B50]/10" : ""
                  } rounded-[.8rem] hover:bg-[#0D9B50]/10 hover:text-[#202224] transition-all ease-in-out`}
                >
                  {subItem.title}
                </div>
              ))}
          </div>
        ))}
      </div>
      <button
        className="bs-navs flex justify-start items-center w-full h-[5rem] text-[1.8rem] my-[.2rem] cursor-pointer rounded-[.8rem] hover:bg-[#0D9B50] hover:text-[#FFF] mt-auto"
        onClick={() => {
          dispatch(logout());
          onClose();
        }}
      >
        <AiOutlineLogout size={20} className="mx-[1.8rem]" />
        Logout
      </button>
    </div>
  );

  return (
    <>
      {/* Mobile Sidebar Overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={onClose}
        >
          <div
            className="fixed top-0 left-0 w-[80%] md:w-[40%] h-full bg-white shadow-lg overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="brand-sidebar w-full h-full py-[4.5rem] flex flex-col justify-between items-start px-[2.4rem]">
              {renderSidebarContent()}
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div className="hidden lg:flex brand-sidebar w-[36rem] py-[3rem] flex-col justify-between items-start pl-[2.4rem] pr-[5rem] select-none">
        {renderSidebarContent()}
      </div>
    </>
  );
};

export default Sidebar;
